import { useMutation, useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { updateBusinessUnitUserMutation } from "./api/mutations";
import { rolesListQuery, type businessUnitUsersQuery } from "./api/queries";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { type ResultOf } from "@/graphql";
import { useToast } from "@/hooks/use-toast";
import { removeNothings } from "@/lib/utils/arrays";
import { extractErrorMessages, getErrorMessage } from "@/lib/utils/errors";
import { formatName } from "@/lib/utils/formatting";

const editUserSchema = z.object({
  roleId: z.string().min(1, "Role is required"),
  status: z.enum(["ACTIVE", "INACTIVE", "INVITED"], {
    error: (issue) => (issue.input === undefined ? "Status is required" : undefined),
  }),
});

type EditUserForm = z.infer<typeof editUserSchema>;

type User = NonNullable<NonNullable<ResultOf<typeof businessUnitUsersQuery>["businessUnitUsers"]>[number]>;

interface UserEditFormProps {
  user: User;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function UserEditForm({ user, open, onOpenChange, onSuccess }: UserEditFormProps) {
  const { toast } = useToast();

  const { data: rolesData, loading: rolesLoading } = useQuery(rolesListQuery);
  const roles = removeNothings(rolesData?.roles || []);

  const form = useForm<EditUserForm>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      roleId: user.role?.id || "",
      status: user.status,
    },
  });

  const [updateUser, { loading }] = useMutation(updateBusinessUnitUserMutation);

  const onSubmit = async (data: EditUserForm) => {
    try {
      const result = await updateUser({
        variables: {
          input: {
            businessUnitUser: parseInt(user.id),
            role: parseInt(data.roleId),
            status: data.status,
          },
        },
      });

      const errors = result.errors || [];
      if (errors.length > 0) {
        toast({
          title: "Failed to update user",
          description: extractErrorMessages(errors),
          variant: "destructive",
        });
        return;
      }

      if (result.data?.updateBusinessUnitUser?.errors?.length) {
        toast({
          title: "Failed to update user",
          description: extractErrorMessages(result.data.updateBusinessUnitUser.errors),
          variant: "destructive",
        });
        return;
      }

      if (result.data?.updateBusinessUnitUser?.businessUnitUser) {
        toast({
          title: "User updated",
          description: `Successfully updated ${formatName({ firstName: user.user.firstName, lastName: user.user.lastName })}`,
        });
        onOpenChange(false);
        onSuccess?.();
      }
    } catch (error) {
      toast({
        title: "Failed to update user",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>
            Update role and status for {formatName({ firstName: user.user.firstName, lastName: user.user.lastName })}.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="roleId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value} disabled={rolesLoading}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {roles.map(
                          (role) =>
                            role && (
                              <SelectItem key={role.id} value={role.id}>
                                {role.name}
                              </SelectItem>
                            ),
                        )}
                      </SelectContent>
                    </Select>
                    <div className="min-h-[20px]">
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={!!user.isOrganisationOwner || user.status === "INVITED"} // Don't allow changing owner or invited user status
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="ACTIVE">Active</SelectItem>
                        <SelectItem value="INACTIVE">Inactive</SelectItem>
                        {user.status === "INVITED" && <SelectItem value="INVITED">Invited</SelectItem>}
                      </SelectContent>
                    </Select>
                    <div className="min-h-[20px]">
                      <FormMessage />
                    </div>
                    {user.isOrganisationOwner && (
                      <p className="text-xs text-muted-foreground">Cannot change status of business unit owner</p>
                    )}
                    {user.status === "INVITED" && !user.isOrganisationOwner && (
                      <p className="text-xs text-muted-foreground">Cannot change status of invited users</p>
                    )}
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="gap-2">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
